import { useCallback, useEffect, useRef, useState } from "react";
import type { Client } from "@stomp/stompjs";
import {
  createClientChatSocket,
  registerClientUser,
  sendClientMessage,
  subscribeClientTopic,
} from "../api/clientChatWebSocket";
import type { ChatMessageView, ChatSocketPayload } from "../types";
import {
  capMessages,
  extractOperatorRecipient,
  normalizeSocketContent,
  resolveUserId,
  socketHistoryToViews,
  socketPayloadToView,
} from "../utils/messages";
import { getUserFacingChatError } from "../utils/chatErrors";

function parseSocketBody(body: string): ChatSocketPayload | null {
  try {
    const parsed = JSON.parse(body) as ChatSocketPayload;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function getMessageType(payload: ChatSocketPayload): string {
  return String(payload.messageType ?? payload.messageTypeEnum ?? "").trim().toUpperCase();
}

export interface UseClientChatResult {
  uuid: string;
  userId: string;
  messages: ChatMessageView[];
  messageText: string;
  setMessageText: (value: string) => void;
  isReady: boolean;
  isSending: boolean;
  isConnecting: boolean;
  error: string | null;
  sendMessage: () => void;
}

export function useClientChat(uuidProp: string): UseClientChatResult {
  const uuid = String(uuidProp ?? "").trim();
  const [userId, setUserId] = useState("");
  const [messages, setMessages] = useState<ChatMessageView[]>([]);
  const [messageText, setMessageText] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientRef = useRef<Client | null>(null);
  const userIdRef = useRef("");
  const operatorRecipientRef = useRef("");
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const messageIdRef = useRef(0);
  const currentTopicRef = useRef("");
  const processIncomingPayloadRef = useRef<(payload: ChatSocketPayload, client?: Client) => void>(
    () => {}
  );

  const appendMessage = useCallback((message: ChatMessageView) => {
    setMessages((prev) => capMessages([...prev, message]));
  }, []);

  const mergeMessages = useCallback((incoming: ChatMessageView[]) => {
    if (incoming.length === 0) return;

    setMessages((prev) => {
      const next = [...prev];
      const indexById = new Map(next.map((item, index) => [item.id, index]));

      for (const item of incoming) {
        const existingIndex = indexById.get(item.id);
        if (typeof existingIndex === "number") {
          next[existingIndex] = item;
        } else {
          next.push(item);
          indexById.set(item.id, next.length - 1);
        }
      }

      return capMessages(next);
    });
  }, []);

  const subscribeToTopic = useCallback((client: Client, topic: string) => {
    const normalizedTopic = String(topic ?? "").trim();
    if (!normalizedTopic) return;

    unsubscribeRef.current?.();
    unsubscribeRef.current = subscribeClientTopic(
      client,
      normalizedTopic,
      uuid,
      (message) => {
        const payload = parseSocketBody(message.body);
        if (!payload) return;
        processIncomingPayloadRef.current(payload, client);
      }
    );
    currentTopicRef.current = normalizedTopic;
  }, [uuid]);

  const processIncomingPayload = useCallback(
    (payload: ChatSocketPayload, client?: Client) => {
      const messageType = getMessageType(payload);
      if (messageType === "ERROR") {
        const message = String(payload.errorMessage ?? "").trim();
        setError(message || "Ошибка сервиса чата");
        return;
      }

      const nextRecipient = extractOperatorRecipient(payload);
      if (nextRecipient) {
        operatorRecipientRef.current = nextRecipient;
      }

      const resolvedUserId = resolveUserId(payload);
      if (resolvedUserId && resolvedUserId !== userIdRef.current) {
        userIdRef.current = resolvedUserId;
        setUserId(resolvedUserId);
      }

      if (client && resolvedUserId && resolvedUserId !== currentTopicRef.current) {
        subscribeToTopic(client, resolvedUserId);
      }

      const content = normalizeSocketContent(payload.content);
      const historyItems = Array.isArray(content?.messages) ? content.messages : [];
      if (historyItems.length > 0) {
        const sortedHistory = [...historyItems].sort((left, right) => {
          const leftTime = new Date(String(left.createdAt ?? "")).getTime();
          const rightTime = new Date(String(right.createdAt ?? "")).getTime();
          if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) return 0;
          return leftTime - rightTime;
        });

        mergeMessages(socketHistoryToViews(sortedHistory, userIdRef.current || uuid));
      }

      if (messageType === "SEND_MESSAGE_USER") {
        const isResultOk = content && typeof content === "object" && content.result === true;
        if (!isResultOk) {
          setError("Не удалось отправить сообщение");
        }
        if (historyItems.length === 0) return;
      }

      const view = socketPayloadToView(
        payload,
        userIdRef.current || uuid,
        `ws-${messageIdRef.current++}`
      );
      if (view) appendMessage(view);
    },
    [appendMessage, mergeMessages, subscribeToTopic, uuid]
  );
  processIncomingPayloadRef.current = processIncomingPayload;

  useEffect(() => {
    if (!uuid) {
      setError("Не указан UUID (маршрут /chat/:uuid)");
      return;
    }

    let disposed = false;
    currentTopicRef.current = "";
    userIdRef.current = uuid;
    setUserId(uuid);
    setMessages([]);
    operatorRecipientRef.current = "";

    setIsConnecting(true);
    setError(null);

    const client = createClientChatSocket({
      authHeader: uuid,
      onConnect: (connectedClient) => {
        if (disposed) return;
        clientRef.current = connectedClient;

        subscribeToTopic(connectedClient, uuid);
        registerClientUser(connectedClient, uuid);

        setIsReady(true);
        setIsConnecting(false);
      },
      onDisconnect: () => {
        setIsReady(false);
      },
      onError: (wsError) => {
        setError(getUserFacingChatError(wsError));
        setIsConnecting(false);
        setIsReady(false);
      },
    });

    clientRef.current = client;
    client.activate();

    return () => {
      disposed = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      clientRef.current?.deactivate();
      clientRef.current = null;
      setIsReady(false);
    };
  }, [uuid, processIncomingPayload, subscribeToTopic]);

  const sendMessage = useCallback(() => {
    const text = messageText.trim();
    const sender = userIdRef.current || uuid;
    const client = clientRef.current;

    if (!text || !sender || !client?.connected) return;

    setIsSending(true);
    setError(null);

    try {
      sendClientMessage(client, text, uuid);

      appendMessage({
        id: `local-${messageIdRef.current++}`,
        text,
        sender,
        isOutgoing: true,
        timeLabel: new Date().toLocaleTimeString("ru-RU", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });
      setMessageText("");
    } catch (sendError) {
      setError(getUserFacingChatError(sendError));
    } finally {
      setIsSending(false);
    }
  }, [appendMessage, messageText, uuid]);

  return {
    uuid,
    userId,
    messages,
    messageText,
    setMessageText,
    isReady,
    isSending,
    isConnecting,
    error,
    sendMessage,
  };
}
